import React from "react";
import { colorForStandard, computeCriticality, resolveConfig } from "./riskAggregates";
import { findingSeverityOf } from "./findingSeverity";

/**
 * 14030 — Per-question Risk Panel building blocks.
 *
 * Exposed as three composable sub-components so VqqQuestionRenderer can
 * place them at the right positions inside the question card:
 *   - <RiskStandardsRow>   — colored standard chips with counts (top)
 *   - <RiskCriticalityBar> — I/L/D + Risk total + criticality tag
 *   - <RiskFindingFooter>  — green-bordered footer strip
 *
 * The default export is the legacy "stacked card" composition that still
 * works for places that don't want to lay them out themselves (e.g.
 * scored-snapshot debug views).
 */

export const RiskStandardsRow = ({ scoredRow, methodology = null }) => {
  if (!scoredRow) return null;
  const groups = parseCitations(scoredRow.regulatory_standards);
  if (groups.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {groups.map((g) => (
        <StandardChip key={g.standard} group={g} methodology={methodology} />
      ))}
    </div>
  );
};

export const RiskCriticalityBar = ({ scoredRow, methodology }) => {
  if (!scoredRow) return null;
  if (isOutOfScope(scoredRow)) return <OutOfScopePill />;

  const impact = numericOrNull(scoredRow.rubric_impact);
  const likelihood = numericOrNull(scoredRow.rubric_likelihood);
  const detectability = numericOrNull(scoredRow.rubric_detectability);
  const riskTotal =
    impact != null && likelihood != null && detectability != null
      ? impact * likelihood * detectability
      : null;
  const criticality = computeCriticality(riskTotal, impact, methodology);

  const anchors = {
    Impact: parseAnchors(methodology?.impact_anchors_json),
    Likelihood: parseAnchors(methodology?.likelihood_anchors_json),
    Detectability: parseAnchors(methodology?.detectability_anchors_json),
  };

  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500">
        Question Criticality
        <span className="ml-1 normal-case text-gray-400 font-normal">
          · based on Impact, Likelihood, and Detectability values
        </span>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <DropdownPill label="I" value={impact} fullName="Impact" anchors={anchors.Impact} />
        <DropdownPill label="L" value={likelihood} fullName="Likelihood" anchors={anchors.Likelihood} />
        <DropdownPill label="D" value={detectability} fullName="Detectability" anchors={anchors.Detectability} inverted />
        <span className="inline-flex items-center gap-1.5 text-[12px] text-gray-700">
          <span className="font-semibold text-gray-900">Risk:</span>
          <span className="tabular-nums font-semibold text-gray-900">
            {riskTotal ?? "—"}
          </span>
        </span>
        <CriticalityBadge criticality={criticality} />
      </div>
    </div>
  );
};

// Cycle order admins step through when clicking the Risk Finding pill.
// Wraps Low → Medium → High → Critical → Compliant → back to Low.
// Not Scored is intentionally absent: it's a display-only state for rows
// the engine couldn't score (no rubric), not a value admins manually set.
const SEVERITY_CYCLE = ["Low", "Medium", "High", "Critical", "Compliant"];

export const RiskFindingFooter = ({ scoredRow, canOverride = false, onOverrideTier = null }) => {
  const [saving, setSaving] = React.useState(false);
  if (!scoredRow) return null;
  if (isOutOfScope(scoredRow)) return null;

  // 14031 AC #8 + 14033 AC #16 — attribution flips from Auto-scored to
  // SME-scored on the first admin touch (severity / gap text / regulatory
  // reference) and stays sticky for the rest of the VQQ's lifecycle. We
  // detect that touch by ANY of: sme_reviewer_email, sme_tier, sme_score,
  // or sme_review_notes being non-empty on the snapshot row.
  const smeScored = isSmeScored(scoredRow);

  // The SME tier wins so an admin's click shows even while the engine's
  // finding_type still says otherwise.
  const severityLabel = findingSeverityOf(scoredRow);
  const palette = SEVERITY_PALETTE[severityLabel] || SEVERITY_PALETTE.Low;

  const reviewer = scoredRow.sme_reviewer_email || "Diligent Risk Model";
  const stamp = scoredRow.sme_reviewed_at
    ? formatTimestamp(scoredRow.sme_reviewed_at)
    : null;

  return (
    <div
      className={`rounded-md border border-gray-200 border-l-4 px-3 py-2.5 flex flex-wrap items-center gap-3 ${palette.stripe}`}
    >
      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">
        Risk Finding
      </span>
      <SeverityPill
        label={severityLabel}
        palette={palette}
        // Admin-only severity override. Clicking cycles to the next tier
        // and patches sme_tier; existing gap notes are preserved.
        clickable={canOverride && !!onOverrideTier}
        saving={saving}
        onClick={async () => {
          if (!canOverride || !onOverrideTier || saving) return;
          const idx = SEVERITY_CYCLE.indexOf(severityLabel);
          const next = SEVERITY_CYCLE[(idx + 1 + SEVERITY_CYCLE.length) % SEVERITY_CYCLE.length];
          setSaving(true);
          try {
            await onOverrideTier(next);
          } finally {
            setSaving(false);
          }
        }}
      />
      {smeScored ? <SmeScoredBadge /> : <AutoScoredBadge />}
      <div className="flex items-center gap-2 ml-auto">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold border border-indigo-200">
          {initialsOf(reviewer)}
        </span>
        <span className="text-[11px] font-medium text-gray-800">{reviewer}</span>
        {stamp ? (
          <>
            <span className="text-gray-300">·</span>
            <span className="text-[11px] text-gray-500 tabular-nums">{stamp}</span>
          </>
        ) : null}
      </div>
    </div>
  );
};

const isSmeScored = (row) => {
  if (!row) return false;
  if (row.sme_reviewer_email && String(row.sme_reviewer_email).trim().length > 0) return true;
  if (row.sme_tier && String(row.sme_tier).trim().length > 0) return true;
  if (row.sme_score != null) return true;
  if (row.sme_review_notes && String(row.sme_review_notes).trim().length > 0) return true;
  return false;
};

const OutOfScopePill = () => (
  <div className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] text-gray-600">
    <ShieldIcon className="h-3.5 w-3.5 text-gray-400" />
    <span>Out of scope — no rubric entry for this question</span>
  </div>
);

const RiskPanelCard = ({ scoredRow, methodology }) => {
  if (!scoredRow) return null;
  return (
    <div className="mt-3 space-y-3">
      <RiskStandardsRow scoredRow={scoredRow} />
      <RiskCriticalityBar scoredRow={scoredRow} methodology={methodology} />
      <RiskFindingFooter scoredRow={scoredRow} />
    </div>
  );
};

const numericOrNull = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const isOutOfScope = (r) =>
  (r.finding_type || "").toLowerCase() === "outofscope" ||
  (r.rubric_impact == null && r.rubric_likelihood == null && r.rubric_detectability == null);

const CRITICALITY_CLASS = {
  Critical: "bg-red-100 text-red-800 border-red-200",
  Major: "bg-orange-100 text-orange-800 border-orange-200",
  Minor: "bg-gray-100 text-gray-700 border-gray-200",
};

const CriticalityBadge = ({ criticality }) => {
  if (!criticality) return null;
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold ${
        CRITICALITY_CLASS[criticality] || CRITICALITY_CLASS.Minor
      }`}
    >
      {criticality}
    </span>
  );
};

// Six per-question Finding Severity buckets, one palette per tier. Stripe
// is the rounded-card left-accent class; pill is the rounded-full pill;
// dot is the inline indicator inside the pill.
const SEVERITY_PALETTE = {
  Critical: {
    stripe: "border-l-red-500 bg-red-50/40",
    pill:   "bg-red-100 text-red-800 border-red-200",
    dot:    "bg-red-500",
  },
  High: {
    stripe: "border-l-orange-500 bg-orange-50/40",
    pill:   "bg-orange-100 text-orange-800 border-orange-200",
    dot:    "bg-orange-500",
  },
  Medium: {
    stripe: "border-l-yellow-500 bg-yellow-50/40",
    pill:   "bg-yellow-100 text-yellow-800 border-yellow-200",
    dot:    "bg-yellow-500",
  },
  Low: {
    stripe: "border-l-blue-500 bg-blue-50/40",
    pill:   "bg-blue-100 text-blue-800 border-blue-200",
    dot:    "bg-blue-500",
  },
  Compliant: {
    stripe: "border-l-green-500 bg-green-50/40",
    pill:   "bg-green-100 text-green-800 border-green-200",
    dot:    "bg-green-500",
  },
  "Not Scored": {
    stripe: "border-l-gray-400 bg-gray-50/40",
    pill:   "bg-gray-100 text-gray-700 border-gray-200",
    dot:    "bg-gray-400",
  },
};

const SeverityPill = ({ label, palette, clickable = false, saving = false, onClick = null }) => {
  if (!clickable) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${palette.pill}`}
      >
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${palette.dot}`} />
        {label}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={saving}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium transition cursor-pointer hover:brightness-95 hover:shadow-sm disabled:opacity-60 disabled:cursor-wait ${palette.pill}`}
      title="Click to override the Finding Severity. Cycles Low → Medium → High → Critical → Compliant."
    >
      {saving ? (
        <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.3" strokeWidth="4" />
          <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
        </svg>
      ) : (
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${palette.dot}`} />
      )}
      {saving ? "Saving…" : label}
    </button>
  );
};

const AutoScoredBadge = () => (
  <span
    className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700"
    title="Auto-scored — finding generated by the Diligent Risk Model from the vendor's response. A Diligent SME may still override this severity before publish."
  >
    <SparkleIcon className="h-3 w-3" />
    Auto-scored
  </span>
);

// 14031 AC #2a + 14033 AC #16 — violet sibling of AutoScoredBadge. Surfaced
// whenever a Diligent admin has touched the finding (severity / gap text /
// regulatory references). Color is intentionally distinct from the blue
// Auto-scored badge so reviewers can scan a section and tell at a glance
// which findings are still as the rubric produced them.
const SmeScoredBadge = () => (
  <span
    className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700"
    title="SME-scored — a Diligent subject-matter expert reviewed this finding and adjusted severity, gap text, or regulatory references."
  >
    <UserCheckIcon className="h-3 w-3" />
    SME-scored
  </span>
);

const UserCheckIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M9 12c2.485 0 4.5-2.015 4.5-4.5S11.485 3 9 3 4.5 5.015 4.5 7.5 6.515 12 9 12zm0 1.5c-3.004 0-9 1.502-9 4.5V21h13.043a8.5 8.5 0 01-.62-3.18 8.5 8.5 0 011.74-5.155A18.5 18.5 0 009 13.5zm10.32 1.18l-4.243 4.244-1.998-2 -1.06 1.062 3.058 3.058 5.302-5.302-1.06-1.062z" />
  </svg>
);

/**
 * Display-only dropdown styled element for I / L / D. Looks like a select
 * (chevron + box) so reviewers visually associate it with the editable
 * admin view, but is not interactive on this read-only tab.
 */
const DropdownPill = ({ label, value, fullName, anchors, inverted }) => {
  const anchor = value != null ? anchors?.[value] : null;
  const inversionNote = inverted
    ? "Inverted scale: higher number = harder to detect = more risk."
    : null;
  const tooltip = [
    `${fullName}: ${value ?? "—"} / 5`,
    anchor ? `${anchor.anchor || ""} — ${anchor.definition || ""}`.trim() : null,
    inversionNote,
    !anchor ? "Methodology copy pending (Scoring Methodology sheet)." : null,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <span className="inline-flex items-center gap-1 text-[12px] cursor-help" title={tooltip}>
      <span className="font-semibold text-gray-700">{label}:</span>
      <span className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-0.5 text-[12px]">
        <span className="font-semibold tabular-nums text-gray-900">{value ?? "—"}</span>
        <ChevronDown className="h-3 w-3 text-gray-400" />
      </span>
    </span>
  );
};

const StandardChip = ({ group, methodology }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] cursor-help ${colorForStandard(group.standard, methodology)}`}
    title={`${group.standard} — ${group.refs.join(" · ")}`}
  >
    <span className="font-semibold">{group.standard}</span>
    <span className="tabular-nums font-semibold">{group.refs.length}</span>
  </span>
);

const ChevronDown = ({ className }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.24 4.38a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
      clipRule="evenodd"
    />
  </svg>
);

const ShieldIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
    <path
      fillRule="evenodd"
      d="M12 2.25a.75.75 0 01.475.17l8.25 6.6a.75.75 0 01.275.585c0 5.32-3.057 9.953-8.45 12.183a.75.75 0 01-.55 0C6.557 19.558 3.5 14.925 3.5 9.605a.75.75 0 01.275-.585l8.25-6.6a.75.75 0 01.475-.17z"
      clipRule="evenodd"
    />
  </svg>
);

const SparkleIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12 2l1.6 4.4L18 8l-4.4 1.6L12 14l-1.6-4.4L6 8l4.4-1.6L12 2zm6 12l1 2.7 2.7 1-2.7 1L18 21.4l-1-2.7-2.7-1 2.7-1L18 14zM5 14l.8 2.2L8 17l-2.2.8L5 20l-.8-2.2L2 17l2.2-.8L5 14z" />
  </svg>
);


const initialsOf = (nameOrEmail) => {
  const raw = String(nameOrEmail || "").trim();
  if (!raw) return "—";
  const localPart = raw.includes("@") ? raw.split("@")[0] : raw;
  const parts = localPart.replace(/[._-]+/g, " ").split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "—";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatTimestamp = (iso) => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString();
  } catch {
    return "";
  }
};

const parseAnchors = (json) => {
  if (!json || typeof json !== "string") return {};
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) return {};
    const out = {};
    for (const item of arr) {
      const lvl = Number(item?.level);
      if (Number.isFinite(lvl)) out[lvl] = item;
    }
    return out;
  } catch {
    return {};
  }
};

const parseCitations = (raw) => {
  if (!raw || typeof raw !== "string") return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .map((g) => ({
          standard: String(g?.standard || g?.title || "").trim(),
          refs: Array.isArray(g?.refs)
            ? g.refs.filter((r) => r != null).map((r) => String(r).trim()).filter(Boolean)
            : g?.data
              ? [String(g.data).trim()]
              : [],
        }))
        .filter((g) => g.standard && g.refs.length > 0);
    }
  } catch {
    // Fallback below for non-JSON text.
  }
  const lines = raw.split("\n").map((s) => s.trim()).filter(Boolean);
  return lines.length === 0 ? [] : [{ standard: "Reference", refs: lines }];
};

export default RiskPanelCard;
